import React, { useEffect, useState } from 'react';
import { Button, Input, Modal, Tooltip, message } from 'antd';
import CustomScrollbar from '../../../../components/CustomScrollbar';
import styles from './DomainSetup.module.scss';
import L from './labels';
import {
	LEVELS,
	CATALOG,
	DEFAULT_DOMAIN,
	accessFrom,
	accessTally,
	clampAccess,
	clone,
	ROLE_NAME_MAX,
	ROLE_DESC_MAX,
	DEFAULT_ROLES_MAX,
	isBuiltInApiRole,
	modulePhrase,
	moduleStat,
	scopedCatalog,
	uid,
} from './catalog';

const cx = (...names) => names.filter(Boolean).join(' ');

/* ---------- Small building blocks ---------- */

// Self-contained toggle so global .ant-switch overrides elsewhere in the app can't distort it.
const Toggle = ({ checked, onChange, label, small }) => (
	<button
		type="button"
		role="switch"
		aria-checked={String(!!checked)}
		aria-label={label}
		className={cx(styles.toggle, small && styles.toggleSm)}
		onClick={() => onChange(!checked)}
	/>
);

export const Segmented = ({ value, onChange, label, small, disabled }) => (
	<div className={cx(styles.seg, small && styles.segSm)} role="radiogroup" aria-label={label} aria-disabled={disabled ? 'true' : undefined}>
		{LEVELS.map((l) => (
			<button
				key={l.value}
				type="button"
				role="radio"
				data-level={l.value}
				aria-checked={String(value === l.value)}
				className={styles.segOpt}
				disabled={disabled}
				onClick={() => !disabled && value !== l.value && onChange(l.value)}>
				{l.label}
			</button>
		))}
	</div>
);

// Domain ceiling card: include or exclude a module and its pages.
const CeilingCard = ({ module, enabled, open, onToggle, onModule, onPage }) => {
	const on = module.pages.filter((p) => enabled[p.id] !== false).length;
	const sum = on === 0
		? L.ceiling.moduleNone
		: module.single
			? L.ceiling.moduleIncluded
		: on === module.pages.length
			? L.ceiling.moduleAll(on)
			: L.ceiling.moduleSome(on, module.pages.length);
	return (
		<section className={styles.mod} data-open={String(open && !module.single)} data-off={String(on === 0)} aria-label={module.name}>
			<div className={styles.modHead}>
				<button type="button" className={styles.modTitle} aria-expanded={module.single ? undefined : String(open)} onClick={module.single ? undefined : onToggle}>
					<span className={styles.modName}>{module.name}</span>
					<span className={styles.modSum}>{sum}</span>
				</button>
				{!module.single && (
					<button type="button" className={styles.modMore} onClick={onToggle}>
						{open ? L.ceiling.hidePages : L.ceiling.showPages(module.pages.length)}
					</button>
				)}
				<Toggle checked={on > 0} onChange={onModule} label={L.ceiling.includeAria(module.name)} />
			</div>
			{open && !module.single && (
				<div className={styles.modPages}>
					{module.pages.map((p) => {
						const en = enabled[p.id] !== false;
						return (
							<div key={p.id} className={styles.row} data-off={String(!en)}>
								<span className={styles.rowName}>{p.name}</span>
								{p.note && <span className={styles.rowNote}>{p.note}</span>}
								<div className={styles.rowCtl}>
									<Toggle small checked={en} onChange={(v) => onPage(p.id, v)} label={L.ceiling.includeAria(p.name)} />
								</div>
							</div>
						);
					})}
				</div>
			)}
		</section>
	);
};

// Role access card: one level for the whole module, or per page when expanded.
//   lockedPages  { pageId: note } pages whose level can't be changed here (shown disabled, with the note).
export const ModuleCard = ({ module, access, open, readOnly, onToggle, onModuleLevel, onPageLevel, lockedPages = {} }) => {
	const s = moduleStat(module, access);
	return (
		<section className={styles.mod} data-open={String(open && !module.single)} data-state={s.open === 0 ? 'hidden' : 'open'} aria-label={module.name}>
			<div className={styles.modHead}>
				<button type="button" className={styles.modTitle} aria-expanded={module.single ? undefined : String(open)} onClick={module.single ? undefined : onToggle}>
					<span className={styles.modName}>{module.name}</span>
					<span className={styles.modSum}>{modulePhrase(module, access)}</span>
				</button>
				{/* Level control + Pages link stay together, so a narrow row wraps them as one group. */}
				<div className={styles.modCtl}>
					<Segmented value={s.uniform} disabled={readOnly} label={L.access.moduleAria(module.name)} onChange={onModuleLevel} />
					{module.single ? (
						// Keeps the access control in the same column as modules that have a Pages link.
						<span className={styles.modMore} aria-hidden="true" />
					) : (
						<button type="button" className={styles.modMore} onClick={onToggle}>
							{open ? L.access.hidePages : L.access.perPage(s.total)}
						</button>
					)}
				</div>
			</div>
			{open && !module.single && (
				<div className={styles.modPages}>
					{module.pages.map((p) => {
						const lvl = access[p.id] || 0;
						const lockNote = lockedPages[p.id];
						const note = lockNote || p.note;
						return (
							<div key={p.id} className={styles.row} data-level={lvl}>
								<span className={styles.rowName}>{p.name}</span>
								{note && <span className={styles.rowNote}>{note}</span>}
								<div className={cx(styles.rowCtl, styles.rowCtlAccess)}>
									<Segmented disabled={readOnly || !!lockNote} value={lvl} label={L.access.moduleAria(p.name)} onChange={(l) => onPageLevel(p.id, l)} />
								</div>
							</div>
						);
					})}
				</div>
			)}
		</section>
	);
};

export const NavPreview = ({ modules, access }) => (
	<aside className={styles.aside} aria-label={L.navPreview.aria}>
		<span className={styles.eyebrow}>{L.navPreview.title}</span>
		<div className={styles.navList}>
			{modules.map((m) => {
				const s = moduleStat(m, access);
				const state = s.open === 0 ? 'hidden' : s.counts[3] ? 'edit' : 'view';
				return (
					<div key={m.id} className={styles.navItem} data-state={state}>
						<span className={styles.navDot} />
						{m.name}
						<span className={styles.navCount}>{s.open ? s.open : ''}</span>
					</div>
				);
			})}
		</div>
	</aside>
);

//   lockedPages  { pageId: note } pages that keep their current level; module-wide changes skip them.
export const AccessEditor = ({ modules, access, openMap, setOpenMap, onAccess, readOnly, lockedPages = {} }) => {
	const set = (ids, level) => {
		const next = { ...access };
		ids.forEach((id) => { if (!lockedPages[id]) next[id] = level; });
		onAccess(next);
	};
	return (
		<div className={styles.split}>
			<div>
				<div className={styles.sectionHead}>
					<h3>{L.access.title}</h3>
					<span className={styles.count}>{accessTally(modules, access)}</span>
				</div>
				<div className={styles.mods}>
					{modules.map((m) => (
						<ModuleCard
							key={m.id}
							module={m}
							access={access}
							readOnly={readOnly}
							lockedPages={lockedPages}
							open={!!openMap[m.id]}
							onToggle={() => setOpenMap({ ...openMap, [m.id]: !openMap[m.id] })}
							onModuleLevel={(l) => set(m.pages.map((p) => p.id), l)}
							onPageLevel={(id, l) => set([id], l)}
						/>
					))}
				</div>
			</div>
			<NavPreview modules={modules} access={access} />
		</div>
	);
};

/* ---------- Domain Setup page ---------- */

// Props:
//   initialDomain  { enabled, defaultRoles } loaded for this domain (defaults to the prototype seed)
//   onSave(domain) persists the domain; may return a promise.
//   onDeleteRole(role) deletes a saved role on the server; returns a promise. Unsaved roles are removed locally.
export const DomainSetup = ({ initialDomain, onSave, onDeleteRole }) => {
	const [domain, setDomain] = useState(() => clone(initialDomain || DEFAULT_DOMAIN));
	const [saved, setSaved] = useState(() => JSON.stringify(initialDomain || DEFAULT_DOMAIN));
	const [selectedId, setSelectedId] = useState(() => {
		const roles = (initialDomain || DEFAULT_DOMAIN).defaultRoles;
		return roles[0] && roles[0].id;
	});
	const [openCeiling, setOpenCeiling] = useState({});
	const [openRole, setOpenRole] = useState({});
	const [flash, setFlash] = useState('');
	const [saving, setSaving] = useState(false);

	// The tab renders before integrations/read responds; adopt the loaded domain when it arrives.
	useEffect(() => {
		if (!initialDomain) return;
		setDomain(clone(initialDomain));
		setSaved(JSON.stringify(initialDomain));
		setSelectedId((id) => (initialDomain.defaultRoles.some((r) => r.id === id) ? id : initialDomain.defaultRoles[0] && initialDomain.defaultRoles[0].id));
	}, [initialDomain]);

	const dirty = JSON.stringify(domain) !== saved;
	const invalid = domain.defaultRoles.some((r) => !r.name.trim());
	const role = domain.defaultRoles.find((r) => r.id === selectedId);
	// Built-in role: locked seed role, or the API marks it type "DEFAULT".
	const isDefaultRole = (r) => !!(r.locked || isBuiltInApiRole(r.api));

	useEffect(() => {
		if (!dirty) return undefined;
		const guard = (e) => { e.preventDefault(); e.returnValue = ''; };
		window.addEventListener('beforeunload', guard);
		return () => window.removeEventListener('beforeunload', guard);
	}, [dirty]);

	// Keep the selected role tab visible in the scrolling tab list (e.g. right after adding a role).
	useEffect(() => {
		const tab = selectedId && document.querySelector('[data-role-tab="' + selectedId + '"]');
		if (tab && tab.scrollIntoView) tab.scrollIntoView({ block: 'nearest', inline: 'nearest' });
	}, [selectedId]);

	useEffect(() => {
		if (!flash) return undefined;
		const t = setTimeout(() => setFlash(''), 2600);
		return () => clearTimeout(t);
	}, [flash]);

	const setEnabled = (patch) => {
		// The domain must keep at least one page; block any change that would switch off the last one.
		const next = { ...domain.enabled, ...patch };
		const anyOn = CATALOG.some((m) => m.pages.some((p) => next[p.id] !== false));
		if (!anyOn) {
			message.warning(L.ceiling.minOnePage);
			return;
		}
		setFlash('');
		setDomain((d) => ({ ...d, enabled: { ...d.enabled, ...patch } }));
	};

	const updateRole = (id, patch) => {
		setFlash('');
		setDomain((d) => ({
			...d,
			defaultRoles: d.defaultRoles.map((r) => (r.id === id ? { ...r, ...patch } : r)),
		}));
	};

	const atRoleLimit = domain.defaultRoles.length >= DEFAULT_ROLES_MAX;

	const addRole = () => {
		if (atRoleLimit) return;
		const r = { id: uid('role'), name: L.roles.newRoleName, desc: '', access: accessFrom(() => 0) };
		setDomain((d) => ({ ...d, defaultRoles: [...d.defaultRoles, r] }));
		setSelectedId(r.id);
		setOpenRole({});
	};

	const removeRole = () => {
		if (!role) return;
		Modal.confirm({
			title: L.roles.deleteConfirmTitle(role.name),
			content: L.roles.deleteConfirmBody,
			okText: L.roles.deleteConfirmOk,
			okType: 'danger',
			cancelText: L.roles.deleteConfirmCancel,
			onOk: () => {
				const drop = () => {
					const rest = domain.defaultRoles.filter((x) => x.id !== role.id);
					setDomain((d) => ({ ...d, defaultRoles: rest }));
					setSelectedId(rest[0] && rest[0].id);
				};
				// A role added here and never saved exists only on this page.
				if (!role.api || !onDeleteRole) return drop();
				// A saved role is deleted on the server right away. Take it out of the saved
				// snapshot too, so Discard doesn't bring it back and other edits stay unsaved.
				// Returning the promise keeps the dialog open with a spinner until the API answers.
				return onDeleteRole(role).then(() => {
					drop();
					setSaved((s) => {
						const snapshot = JSON.parse(s);
						snapshot.defaultRoles = snapshot.defaultRoles.filter((x) => x.id !== role.id);
						return JSON.stringify(snapshot);
					});
				});
			},
		});
	};

	const discard = () => {
		const restored = JSON.parse(saved);
		setDomain(restored);
		if (!restored.defaultRoles.some((r) => r.id === selectedId)) {
			setSelectedId(restored.defaultRoles[0] && restored.defaultRoles[0].id);
		}
		setFlash('');
	};

	const save = async () => {
		const next = {
			...domain,
			defaultRoles: domain.defaultRoles.map((r) => ({ ...r, access: clampAccess(r.access, domain) })),
		};
		setSaving(true);
		try {
			if (onSave) await onSave(next);
			setDomain(next);
			setSaved(JSON.stringify(next));
			setFlash(L.saveBar.saved);
		} catch (e) {
			// onSave may already have shown the API's error message
			if (!(e && e.messageShown)) message.error(L.saveBar.saveFailed);
		} finally {
			setSaving(false);
		}
	};

	let totalPages = 0;
	let onPages = 0;
	CATALOG.forEach((m) => m.pages.forEach((p) => {
		totalPages++;
		if (domain.enabled[p.id] !== false) onPages++;
	}));

	const modules = scopedCatalog(domain);

	return (
		<CustomScrollbar className="authorize-admin-page-scroller">
			<div className={styles.page}>
				<div className={styles.pageHead}>
					<p>{L.page.description}</p>
				</div>

				<section className={styles.panel} aria-labelledby="ds-ceiling-title">
					<div className={styles.panelHead}>
						<div>
							<h3 id="ds-ceiling-title">{L.ceiling.title}</h3>
							<p>{L.ceiling.description}</p>
						</div>
						<span className={styles.count}>{L.ceiling.count(onPages, totalPages)}</span>
					</div>
					<div className={styles.panelBody}>
						<div className={styles.modsGrid}>
							{CATALOG.map((m) => (
								<CeilingCard
									key={m.id}
									module={m}
									enabled={domain.enabled}
									open={!!openCeiling[m.id]}
									onToggle={() => setOpenCeiling({ ...openCeiling, [m.id]: !openCeiling[m.id] })}
									onModule={(v) => {
										const patch = {};
										m.pages.forEach((p) => { patch[p.id] = v; });
										setEnabled(patch);
									}}
									onPage={(id, v) => setEnabled({ [id]: v })}
								/>
							))}
						</div>
					</div>
				</section>

				<section className={styles.panel} aria-labelledby="ds-roles-title">
					<div className={styles.panelHead}>
						<div>
							<h3 id="ds-roles-title">{L.roles.title}</h3>
							<p>{L.roles.description}</p>
						</div>
					</div>
					<div className={styles.tabs}>
						{/* Role tabs sit in one row and scroll horizontally. */}
						<div className={styles.tabList} role="tablist">
							{domain.defaultRoles.map((r) => (
								<button
									key={r.id}
									type="button"
									role="tab"
									className={styles.tab}
									data-role-tab={r.id}
									aria-selected={String(r.id === selectedId)}
									onClick={() => setSelectedId(r.id)}>
									{r.name || L.roles.untitled}
								</button>
							))}
						</div>
						{/* Disabled at the limit; the span lets the tooltip show on a disabled button. */}
						<Tooltip title={atRoleLimit ? L.roles.maxRolesReached(DEFAULT_ROLES_MAX) : ''}>
							<span className={styles.addRole}>
								<Button type="link" disabled={atRoleLimit} onClick={addRole}>{L.roles.addRole}</Button>
							</span>
						</Tooltip>
					</div>
					<div className={styles.panelBody}>
						{!role ? (
							<p className={styles.hint}>{L.roles.empty}</p>
						) : (
							<>
								{role.locked && <p className={styles.lockedNote}>{L.roles.lockedNote}</p>}								<div className={styles.fields}>
									<div className={styles.field}>
										<label className={styles.label} htmlFor="ds-role-name">
											<span className={styles.req}>{L.roles.requiredMark}</span>{L.roles.nameLabel}
											<span className={styles.charCount}>{L.roles.charCount(role.name.length, ROLE_NAME_MAX)}</span>
										</label>
										<Input
											id="ds-role-name"
											value={role.name}
											placeholder={L.roles.namePlaceholder}
											maxLength={ROLE_NAME_MAX}
											disabled={role.locked}
											className={!role.name.trim() ? styles.inputError : ''}
											onChange={(e) => updateRole(role.id, { name: e.target.value })}
										/>
									</div>
									<div className={cx(styles.field, styles.fieldWide)}>
										<label className={styles.label} htmlFor="ds-role-desc">
											{L.roles.descLabel}
											<span className={styles.charCount}>{L.roles.charCount((role.desc || '').length, ROLE_DESC_MAX)}</span>
										</label>
										<Input
											id="ds-role-desc"
											value={role.desc || ''}
											// Default roles with no description show an empty field, not the placeholder.
											placeholder={isDefaultRole(role) && !(role.desc || '').trim() ? '' : L.roles.descPlaceholder}
											maxLength={ROLE_DESC_MAX}
											disabled={role.locked}
											onChange={(e) => updateRole(role.id, { desc: e.target.value })}
										/>
									</div>
									{!role.locked && domain.defaultRoles.length > 1 && (
										<Button className={cx('secondary-btn btn-padding-lg', styles.dangerBtn)} onClick={removeRole}>
											{L.roles.deleteRole}
										</Button>
									)}
								</div>
								<AccessEditor
									modules={modules}
									access={clampAccess(role.access, domain)}
									openMap={openRole}
									setOpenMap={setOpenRole}
									readOnly={role.locked}
									onAccess={(next) => updateRole(role.id, { access: next })}
								/>
							</>
						)}
					</div>
				</section>

				{/* Footer pinned to the bottom, like the Create A Role footer. Buttons are always shown and enable once there are changes. */}
				<div className={styles.saveBar} role="status">
					<span className={styles.saveStatus}>{flash || (dirty ? (invalid ? L.saveBar.nameRequired : L.saveBar.unsaved) : '')}</span>
					<Button className="secondary-btn btn-padding-lg" disabled={!dirty} onClick={discard}>{L.saveBar.discard}</Button>
					<Button className="primary-btn btn-padding-lg" disabled={!dirty || invalid} loading={saving} onClick={save}>
						{L.saveBar.save}
					</Button>
				</div>
			</div>
		</CustomScrollbar>
	);
};

export default DomainSetup;
